import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/button';
import { Activity } from 'lucide-react';
import {
  claimAssessment,
  getMyAssessments,
  shareAssessmentResults,
  type MyAssessment,
} from '../api/careSeekerApi';
import { readAssessmentClaimToken, clearAssessmentClaimToken } from '../hooks/useTakeAssessment';

const MINE_KEY = ['longevity-assessment', 'mine'];

/**
 * Care Seeker dashboard Longevity card (Feature 095, Slice 3). Shown above Care
 * Guides. On mount it claims any assessment stashed at signup (token → account),
 * then surfaces the client's completed assessment with a link to their results and
 * a post-hoc "Share with my advisor" action when it isn't shared yet.
 */
export function CareSeekerLongevityCard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const claimedRef = useRef(false);

  const { data: assessments } = useQuery<MyAssessment[]>({
    queryKey: MINE_KEY,
    queryFn: getMyAssessments,
    retry: false,
  });

  // Claim the assessment carried across signup (once), then refresh.
  useEffect(() => {
    if (claimedRef.current) return;
    const token = readAssessmentClaimToken();
    if (!token) return;
    claimedRef.current = true;
    claimAssessment(token)
      .then(() => {
        clearAssessmentClaimToken();
        queryClient.invalidateQueries({ queryKey: MINE_KEY });
      })
      .catch(() => {
        // Keep the token and re-arm so the next mount can retry: the failure
        // may be transient, or the account's email may not match the invite
        // yet. Only a successful claim clears the token.
        claimedRef.current = false;
      });
  }, [queryClient]);

  const share = useMutation({
    mutationFn: (id: string) => shareAssessmentResults(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MINE_KEY }),
  });

  const completed = assessments?.find((a) => a.status === 'COMPLETED');
  if (!completed) return null;

  return (
    <div className="md:col-span-2 flex flex-col items-start justify-between gap-4 rounded-2xl border border-gray-200 bg-white p-6 sm:flex-row sm:items-center sm:p-8">
      <div className="flex items-start gap-3">
        <Activity className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#026857]" />
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Longevity Readiness Assessment</h2>
          <p className="mt-1 text-sm text-gray-600">
            Completed{completed.overallScore != null ? ` — score ${completed.overallScore}` : ''}. Review
            your score, planning gaps, and recommended next steps.
          </p>
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-wrap gap-2">
        {completed.shareStatus === 'NONE' && (
          <Button
            variant="outline"
            disabled={share.isPending}
            onClick={() => share.mutate(completed.id)}
          >
            {share.isPending ? 'Sharing…' : 'Share with my advisor'}
          </Button>
        )}
        <Button
          className="bg-[#026857] text-white hover:bg-[#025247]"
          onClick={() => navigate(`/longevity-assessment/results/${completed.id}`)}
        >
          View My Results
        </Button>
      </div>
    </div>
  );
}
